import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { employeeService, type Employee } from '../../api/Employee';
import { incidentService, type Incident, type CreateIncidentData } from '../../api/incidents';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import { getApiErrorMessage } from '../../api';
import {
  AlertTriangle,
  Search,
  RefreshCw,
  Car,
  MapPin,
  CalendarDays,
  Clock,
  AlignLeft,
  Image as ImageIcon,
  User,
  Eye,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

const formatDate = (value?: string | Date | null) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const ITEMS_PER_PAGE = 20;

const IncidentesPage = () => {
  const { setPageTitle } = useAuth();
  const [incidentes, setIncidentes] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateIncidentData> & { involved_employees?: number[] }>({
    vehicle_id: undefined,
    date: '',
    time: '',
    description: '',
    location: '',
    involved_employees: [],
  });
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Incidentes do setor');
  }, [setPageTitle]);

  const fetchIncidentes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await incidentService.getAll();
      setIncidentes(data);
      setVehicles((prev) =>
        Array.from(
          new Map(
            [...prev, ...data.filter((item) => item.vehicle).map((item) => item.vehicle as Vehicle)]
              .map((vehicle) => [vehicle.id, vehicle]),
          ).values(),
        ),
      );
    } catch (err: unknown) {
      setIncidentes([]);
      setError(getApiErrorMessage(err, 'Erro ao carregar incidentes.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScopedReferences = useCallback(async () => {
    try {
      const [scopedVehicles, scopedEmployees] = await Promise.all([
        vehicleService.getAllVehicles(),
        employeeService.getAllEmployees(),
      ]);
      setVehicles(scopedVehicles);
      setEmployees(scopedEmployees);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao carregar referências permitidas para incidentes.'));
    }
  }, []);

  useEffect(() => {
    void fetchIncidentes();
    void fetchScopedReferences();
  }, [fetchIncidentes, fetchScopedReferences]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'select-multiple') {
      const select = e.target as HTMLSelectElement;
      const selectedOptions = Array.from(select.selectedOptions).map(option => Number(option.value));
      setFormData(prev => ({ ...prev, [name]: selectedOptions }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setPhotoFiles(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemovePhoto = (idx: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== idx));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = () => {
    setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: [] });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const imagesBase64 = await Promise.all(photoFiles.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }));
      const payload: CreateIncidentData & { images?: string[] } = {
        vehicle_id: Number(formData.vehicle_id),
        date: formData.date || '',
        time: formData.time || '',
        description: formData.description || '',
        location: formData.location || '',
        involved_employees: formData.involved_employees || [],
        ...(imagesBase64.length > 0 ? { images: imagesBase64 } : {}),
      };
      await incidentService.create(payload);
      setIsModalOpen(false);
      setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: [] });
      setPhotoFiles([]);
      setPhotoPreviews([]);
      await fetchIncidentes();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar incidente.'));
    } finally {
      setLoading(false);
    }
  };

  const filteredIncidentes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return incidentes;
    return incidentes.filter(i =>
      [
        i.id.toString(),
        i.description || '',
        i.location || '',
        i.vehicle?.brand || '',
        i.vehicle?.license_plate || '',
        i.reported_by_user?.name || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [incidentes, searchTerm]);

  const canCreateIncident = useMemo(
    () => vehicles.length > 0,
    [vehicles.length],
  );

  const totalPages = Math.max(1, Math.ceil(filteredIncidentes.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredIncidentes.length, currentPage, totalPages]);

  const { paginatedIncidentes, pageRange } = useMemo(() => {
    const total = filteredIncidentes.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredIncidentes.slice(startIndex, endIndex);
    return {
      paginatedIncidentes: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredIncidentes, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderInvolvedEmployees = (ids: number[] = []) => (
    <div className="flex flex-col gap-0.5">
      {ids.length === 0 && <span className="text-[13px] text-slate-400">Nenhum envolvido informado.</span>}
      {ids.map(id => {
        const emp = employees.find(e => e.id === id);
        return emp ? (
          <div key={id} className="text-[13px] text-slate-900">
            <span className="font-semibold">{emp.name}</span>
            <span className="ml-2 text-[11.5px] text-slate-500">{emp.cargo}</span>
          </div>
        ) : null;
      })}
    </div>
  );

  const resolveVehicle = (item: Incident) => {
    if (item.vehicle?.brand && item.vehicle.license_plate) return item.vehicle;
    const fallback = vehicles.find(v => v.id === item.vehicle_id);
    return fallback ?? { brand: `Veículo #${item.vehicle_id}`, license_plate: '-' as string | undefined };
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Incidentes do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Incidentes
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Registre e acompanhe os incidentes
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                do seu departamento e equipe
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              disabled={loading || !canCreateIncident}
              className="h-10 px-4"
            >
              <Plus className="h-4 w-4 stroke-[2]" />
              Novo Incidente
            </Button>
            <Button variant="secondary" onClick={() => void fetchIncidentes()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {!canCreateIncident && (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
            O cadastro de incidente exige ao menos um veículo acessível no seu setor. Sem essa referência, a criação fica temporariamente bloqueada.
          </div>
        )}

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.6)]">
                <AlertTriangle className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Incidentes
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum incidente encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} incidentes listados (total cadastrado: ${incidentes.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-incidents"
                placeholder="Pesquisar descrição, local, veículo, placa ou reportado por..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando incidentes...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Local</Th>
                      <Th>Data</Th>
                      <Th>Hora</Th>
                      <Th>Descrição</Th>
                      <Th>Fotos</Th>
                      <Th>Criado em</Th>
                      <Th>Reportado por</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum incidente encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedIncidentes.map((item) => {
                        const vehicle = resolveVehicle(item);
                        return (
                          <Tr key={item.id}>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.55)]">
                                  <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900" title={vehicle.brand}>
                                    {vehicle.brand}
                                  </div>
                                  <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                    {vehicle.license_plate || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5">
                                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <span className="block truncate font-medium text-slate-700" title={item.location || '-'}>
                                  {item.location || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {formatDate(item.date)}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {item.time || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5 w-full">
                                <AlignLeft className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <span className="block truncate font-medium text-slate-700 max-w-[320px]" title={item.description || '-'}>
                                  {item.description || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              {item.images && item.images.length > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="flex -space-x-2">
                                    {item.images.slice(0, 3).map((img, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setSelectedImage(img.url)}
                                        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
                                        title={`Foto ${idx + 1} de ${item.images!.length}`}
                                      >
                                        <img src={img.url} alt={`Incidente ${item.id}`} className="h-full w-full object-cover" />
                                      </button>
                                    ))}
                                  </div>
                                  {item.images.length > 1 && (
                                    <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 text-[11.5px] font-bold text-slate-700 ring-1 ring-inset ring-slate-200 tabular-nums">
                                      <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
                                      {item.images.length}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[12px] font-medium text-slate-400">Sem fotos</span>
                              )}
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <div className="flex flex-col leading-tight">
                                  <span className="font-semibold text-slate-700 tabular-nums text-[12.5px]">
                                    {formatDate(item.created_at)}
                                  </span>
                                  <span className="text-[11.5px] text-slate-500 tabular-nums">
                                    {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200">
                                  <User className="h-4 w-4 stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900">
                                    {item.reported_by_user?.name || '-'}
                                  </div>
                                  <div className="mt-0.5 text-[11.5px] font-medium text-slate-500 truncate uppercase tracking-wide">
                                    {item.reported_by_user?.role || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setSelectedIncident(item)}
                                  className="!h-8 !px-2"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">
                    Registrar Novo Incidente
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Preencha os campos abaixo para cadastrar um novo incidente no seu setor.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Veículo <span className="text-rose-600">*</span></label>
                    <select
                      name="vehicle_id"
                      value={formData.vehicle_id || ''}
                      onChange={handleInputChange}
                      className="h-11 rounded-lg border border-slate-300 px-3.5 text-[14px] font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                      required
                    >
                      <option value="" disabled>Selecione o veículo</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.brand || '(Sem marca)'} — Placa: {vehicle.license_plate}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-2">
                      <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Data <span className="text-rose-600">*</span></label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleInputChange}
                        className="h-11 rounded-lg border border-slate-300 px-3.5 text-[14px] font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Hora <span className="text-rose-600">*</span></label>
                      <input
                        type="time"
                        name="time"
                        value={formData.time || ''}
                        onChange={handleInputChange}
                        className="h-11 rounded-lg border border-slate-300 px-3.5 text-[14px] font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Local do Incidente <span className="text-rose-600">*</span></label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location || ''}
                      onChange={handleInputChange}
                      placeholder="Ex: Avenida Paulista, 1000 — São Paulo/SP"
                      className="h-11 rounded-lg border border-slate-300 px-3.5 text-[14px] font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Descrição <span className="text-rose-600">*</span></label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Descreva com clareza o que aconteceu, como aconteceu e se houve danos ou feridos..."
                      className="rounded-lg border border-slate-300 px-3.5 py-3 text-[14px] font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition resize-y"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Funcionários Envolvidos <span className="text-slate-400 font-normal normal-case tracking-normal">(opcional, segure Ctrl/Cmd para múltiplos)</span></label>
                    <select
                      name="involved_employees"
                      multiple
                      value={(formData.involved_employees || []).map(String)}
                      onChange={handleInputChange}
                      className="min-h-[8rem] rounded-lg border border-slate-300 px-3.5 py-2.5 text-[14px] font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                    >
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} — {emp.cargo} {emp.cpf ? `(${emp.cpf})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Fotos / Evidências <span className="text-slate-400 font-normal normal-case tracking-normal">(opcional, múltiplas permitidas)</span></label>
                    <label className="flex h-auto min-h-[56px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-5 py-4 text-center transition-all hover:border-blue-400 hover:bg-blue-50/50">
                      <ImageIcon className="h-6 w-6 stroke-[1.8] text-slate-500" />
                      <div className="text-[13px] font-semibold text-slate-700">Clique aqui para selecionar fotos</div>
                      <div className="text-[11.5px] text-slate-500">PNG, JPG ou WEBP (até 20MB cada)</div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </label>
                    {photoPreviews.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2.5">
                        {photoPreviews.map((src, idx) => (
                          <div key={idx} className="relative group">
                            <img src={src} alt={`Preview ${idx + 1}`} className="h-20 w-20 object-cover rounded-xl border-2 border-white shadow-md" />
                            <button
                              type="button"
                              onClick={() => handleRemovePhoto(idx)}
                              className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-rose-600 text-white shadow-lg ring-2 ring-white transition-transform hover:scale-110"
                              title="Remover imagem"
                            >
                              <X className="h-3.5 w-3.5 stroke-[2.2]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end md:col-span-2 mt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsModalOpen(false)}
                      disabled={loading}
                      className="h-10 px-5"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      className="h-10 px-5"
                    >
                      <Plus className="h-4 w-4 stroke-[2]" />
                      {loading ? 'Salvando...' : 'Registrar Incidente'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedIncident && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">
                    Detalhes do Incidente
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Registro #{selectedIncident.id} • Criado em {formatDateTime(selectedIncident.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedIncident(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                {(() => {
                  const vehicle = resolveVehicle(selectedIncident);
                  return (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Veículo envolvido</p>
                        <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">{vehicle.brand}</p>
                        <p className="mt-0.5 text-[12.5px] text-slate-600 font-mono">{vehicle.license_plate || '-'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Local do ocorrido</p>
                        <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">{selectedIncident.location || '-'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Data do incidente</p>
                        <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">{formatDate(selectedIncident.date)}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Hora do incidente</p>
                        <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">{selectedIncident.time || '-'}</p>
                      </div>
                      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Descrição completa</p>
                        <p className="mt-1.5 text-[14px] font-medium text-slate-800 whitespace-pre-wrap leading-relaxed">
                          {selectedIncident.description || '-'}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Reportado por</p>
                        <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">{selectedIncident.reported_by_user?.name || '-'}</p>
                        <p className="mt-0.5 text-[11.5px] font-bold text-slate-500 uppercase tracking-wide">{selectedIncident.reported_by_user?.role || '-'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Funcionários envolvidos</p>
                        <div className="mt-1.5">
                          {renderInvolvedEmployees(selectedIncident.involved_employees)}
                        </div>
                      </div>
                      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-2.5">Fotos / Evidências do incidente</p>
                        {selectedIncident.images && selectedIncident.images.length > 0 ? (
                          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                            {selectedIncident.images.map((img, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setSelectedImage(img.url)}
                                className="group relative aspect-square overflow-hidden rounded-xl border-2 border-white shadow-sm transition-all hover:shadow-lg hover:ring-2 hover:ring-blue-500"
                                title={`Abrir foto ${idx + 1}`}
                              >
                                <img src={img.url} alt={`Incidente ${selectedIncident.id} - foto ${idx + 1}`} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 flex items-end p-2">
                                  <span className="text-[11px] font-bold text-white">Abrir foto</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[13px] font-medium text-slate-500">
                            <ImageIcon className="h-4 w-4 stroke-[1.8]" />
                            Nenhuma foto foi anexada a este incidente.
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setSelectedIncident(null)}
                    className="h-10 px-5"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-80 flex items-center justify-center px-4 py-6 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative w-full max-w-6xl"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -top-3 -right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.4)] transition-transform hover:scale-110"
              aria-label="Fechar visualização"
            >
              <X className="h-5 w-5 stroke-[2.1]" />
            </button>
            <img
              src={selectedImage}
              alt="Foto em tamanho real"
              className="max-h-[85vh] w-auto max-w-full mx-auto rounded-2xl shadow-[0_20px_60px_-12px_rgba(0,0,0,0.6)] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentesPage;
